// src/pages/Register.jsx
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { User, Mail, Lock, Baby, Eye, EyeOff, Phone } from "lucide-react";
import SEO from "../components/SEO";
export default function Register() {
  const { register: registerUser } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get("plan") || "free";

  const { register, handleSubmit, formState: { errors }, watch } = useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPass, setShowPass] = useState(false);

  async function onSubmit(data) {
    setLoading(true);
    setError("");
    try {
      await registerUser({
        email: data.email,
        password: data.password,
        name: data.name,
        role: "parent",
        phone: data.phone,
        childAge: data.childAge,
        childName: data.childName
      });
      if (plan === "paid") {
        navigate("/payment");
      } else {
        navigate("/consult/free");
      }
    } catch (err) {
      const msgs = {
        "auth/email-already-in-use": lang === "ar" ? "البريد الإلكتروني مستخدم بالفعل" : "Email already in use",
        "auth/weak-password": lang === "ar" ? "كلمة المرور ضعيفة جداً" : "Password too weak"
      };
      setError(msgs[err.code] || (lang === "ar" ? "حدث خطأ، حاول مجدداً" : "An error occurred"));
    }
    setLoading(false);
  }

  return (
    <div className="page-content fade-in" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem 1.25rem" }}>
      <SEO 
        title={lang === "ar" ? "إنشاء حساب" : "Register"} 
        description={lang === "ar" ? "قم بإنشاء حساب جديد في منصة نظم للبدء." : "Create a new account on the Nodom platform to get started."}
        canonicalUrl="https://www.nodom.site/register"
      />
      <div style={{ width: "100%", maxWidth: 460 }}>
        <div className="text-center" style={{ marginBottom: "2rem" }}>
          <div style={{ fontSize: "2rem", fontWeight: 800, color: "var(--color-forest)", marginBottom: "0.5rem" }}>نظم</div>
          <h2>{t("register")}</h2>
          {plan === "paid" && (
            <div className="badge badge-green" style={{ marginTop: "0.5rem" }}>
              {lang === "ar" ? "اشتراك مدفوع — 1000 د.ج/شهر" : "Paid Plan — 1000 DZD/month"}
            </div>
          )}
        </div>

        <div className="card card-lg">
          {error && <div className="alert alert-error">{error}</div>}

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="form-group">
              <label className="form-label"><User size={14} style={{ display: "inline", marginLeft: "0.3rem" }} />{t("nameLabel")}</label>
              <input
                className={`form-input ${errors.name ? "error" : ""}`}
                type="text"
                placeholder={lang === "ar" ? "الاسم الكامل" : "Full name"}
                {...register("name", { required: lang === "ar" ? "الاسم مطلوب" : "Name required" })}
              />
              {errors.name && <div className="form-error">{errors.name.message}</div>}
            </div>

            <div className="form-group">
              <label className="form-label"><Mail size={14} style={{ display: "inline", marginLeft: "0.3rem" }} />{t("emailLabel")}</label>
              <input
                className={`form-input ${errors.email ? "error" : ""}`}
                type="email" dir="ltr"
                placeholder="example@email.com"
                {...register("email", {
                  required: lang === "ar" ? "البريد مطلوب" : "Email required",
                  pattern: { value: /^\S+@\S+\.\S+$/, message: lang === "ar" ? "بريد غير صالح" : "Invalid email" }
                })}
              />
              {errors.email && <div className="form-error">{errors.email.message}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">
                <Phone size={14} style={{ display: "inline", marginLeft: "0.3rem" }} />
                {lang === "ar" ? "رقم الهاتف" : "Phone Number"}
              </label>
              <input
                className={`form-input ${errors.phone ? "error" : ""}`}
                type="tel"
                dir="ltr"
                placeholder="0550 000 000"
                maxLength={10}
                onKeyPress={e => !/[0-9]/.test(e.key) && e.preventDefault()}
                {...register("phone", {
                  required: lang === "ar" ? "رقم الهاتف مطلوب" : "Phone number required",
                   pattern: {
                    value: /^0[5-7][0-9]{8}$/,
                    message: lang === "ar" ? "رقم غير صالح — مثال: 0500000000" : "Invalid — example: 0500000000"
                 }
                })}
              />
              {errors.phone && <div className="form-error">{errors.phone.message}</div>}
            </div>

            <div className="form-group">
              <label className="form-label"><Lock size={14} style={{ display: "inline", marginLeft: "0.3rem" }} />{t("passwordLabel")}</label>
              <div style={{ position: "relative" }}>
                <input
                  className={`form-input ${errors.password ? "error" : ""}`}
                  type={showPass ? "text" : "password"}
                  dir="ltr" placeholder="••••••••"
                  {...register("password", {
                    required: lang === "ar" ? "كلمة المرور مطلوبة" : "Password required",
                    minLength: { value: 6, message: lang === "ar" ? "٦ أحرف على الأقل" : "Min 6 characters" }
                  })}
                />
                <button type="button" onClick={() => setShowPass(s => !s)}
                  style={{ position: "absolute", top: "50%", left: lang === "ar" ? "0.75rem" : "auto", right: lang === "ar" ? "auto" : "0.75rem", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--color-gray-400)" }}>
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && <div className="form-error">{errors.password.message}</div>}
            </div>

            <div className="grid-2" style={{ gap: "1rem" }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label"><Baby size={14} style={{ display: "inline", marginLeft: "0.3rem" }} />{lang === "ar" ? "اسم الطفل" : "Child's Name"}</label>
                <input className="form-input" type="text"
                  placeholder={lang === "ar" ? "اختياري" : "Optional"}
                  {...register("childName")}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t("childAgeLabel")}</label>
                <select className="form-select" {...register("childAge")}>
                  <option value="">{lang === "ar" ? "-- اختر --" : "-- Select --"}</option>
                  {Array.from({ length: 14 }, (_, i) => i + 3).map(age => (
                    <option key={age} value={age}>{age}</option>
                  ))}
                </select>
              </div>
            </div>

            <button type="submit" className="btn btn-primary btn-full" disabled={loading} style={{ marginTop: "1.5rem" }}>
              {loading ? <div className="spinner" style={{ width: 20, height: 20 }} /> : t("registerBtn")}
            </button>
          </form>

          <div className="divider-text">{lang === "ar" ? "أو" : "or"}</div>
          <p className="text-center text-sm">
            {t("hasAccount")}{" "}
            <Link to="/login" style={{ fontWeight: 700 }}>{t("login")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
